import * as XLSX from 'xlsx';

export interface ParsedEnquiryRow {
  enquiryId: number | null;
  firstName: string | null;
  lastName: string | null;
  dateOfBirth: string | null;
  gender: string | null;
  email: string | null;
  mobile: string | null;
  telephone: string | null;
  aadharNumber: string | null;
  referredBy: string | null;
  referredByMobile: string | null;
  referredByEmail: string | null;
  counsellor: string | null;
  enquiryCentre: string | null;
  remarks: string | null;
  admissionRemark: string | null;
  admissionNo: string | null;
  hallTicket: string | null;
  enquiryFormNumber: string | null;
  fatherName: string | null;
  fatherMobile: string | null;
  fatherEmail: string | null;
  motherName: string | null;
  motherMobile: string | null;
  motherEmail: string | null;
  customFields?: Record<string, any>;
}

/**
 * Header text (case-insensitive, trimmed) → field key mapping.
 * Must match the headers produced by bulk-update-export.controller.ts.
 */
const HEADER_MAP: Record<string, keyof ParsedEnquiryRow> = {
  'enquiry id': 'enquiryId',
  'first name': 'firstName',
  'last name': 'lastName',
  'date of birth': 'dateOfBirth',
  'gender': 'gender',
  'email': 'email',
  'mobile': 'mobile',
  'telephone': 'telephone',
  'aadhar number': 'aadharNumber',
  'referred by': 'referredBy',
  'referred by mobile': 'referredByMobile',
  'referred by email': 'referredByEmail',
  'counsellor': 'counsellor',
  'enquiry centre': 'enquiryCentre',
  'remarks': 'remarks',
  'admission remark': 'admissionRemark',
  'admission no': 'admissionNo',
  'hall ticket': 'hallTicket',
  'form number': 'enquiryFormNumber',
  'father name': 'fatherName',
  'father mobile': 'fatherMobile',
  'father email': 'fatherEmail',
  'mother name': 'motherName',
  'mother mobile': 'motherMobile',
  'mother email': 'motherEmail',
};

/**
 * Normalize an Excel date value to a string.
 * Handles: Excel serial dates, Date objects, DD/MM/YYYY strings, YYYY-MM-DD strings.
 */
function normalizeDate(val: any): string | null {
  if (val === null || val === undefined || val === '') return null;

  // Excel serial number (e.g. 44927)
  if (typeof val === 'number') {
    const d = XLSX.SSF.parse_date_code(val);
    if (d) {
      const dd = String(d.d).padStart(2, '0');
      const mm = String(d.m).padStart(2, '0');
      return `${dd}/${mm}/${d.y}`;
    }
  }

  // Date object
  if (val instanceof Date) {
    const dd = String(val.getDate()).padStart(2, '0');
    const mm = String(val.getMonth() + 1).padStart(2, '0');
    return `${dd}/${mm}/${val.getFullYear()}`;
  }

  // Already a string — return as-is (backend will normalize)
  return String(val).trim() || null;
}

function cellToString(val: any): string | null {
  if (val === null || val === undefined) return null;
  const s = String(val).trim();
  return s || null;
}

/**
 * Parse the "Student Data" sheet from a bulk-update Excel workbook.
 *
 * Layout (matching export):
 * - Row 1: Title row (merged, skip)
 * - Row 2: Column headers
 * - Row 3+: Data rows
 */
export function parseBulkUpdateSheet(buffer: ArrayBuffer): ParsedEnquiryRow[] {
  const wb = XLSX.read(buffer, { type: 'array', cellDates: true });

  // Read Sheet 1 (by name or index 0)
  const sheetName = wb.SheetNames[0];
  const ws = wb.Sheets[sheetName];
  if (!ws) throw new Error('No worksheet found in the uploaded file');

  // Convert to array of arrays (raw)
  const raw: any[][] = XLSX.utils.sheet_to_json(ws, { header: 1, defval: null, raw: true });

  if (raw.length < 3) {
    throw new Error('File must have at least a title row, a header row, and one data row');
  }

  // Row 1 is title (skip), Row 2 is headers (index 1)
  const headerRowRaw = raw[1];
  if (!headerRowRaw) throw new Error('Header row not found (expected at row 2)');

  // Known readonly headers to skip (not editable, not custom fields)
  const SKIP_HEADERS = new Set(['student name']);

  // Map column index → field key (known fields) or original header text (custom fields)
  const colMap: Array<keyof ParsedEnquiryRow | null> = [];
  const customFieldColMap: Array<{ colIndex: number; headerLabel: string }> = [];

  headerRowRaw.forEach((h: any, idx: number) => {
    if (!h) { colMap.push(null); return; }
    const headerText = String(h).trim();
    const key = headerText.toLowerCase();
    if (HEADER_MAP[key]) {
      colMap.push(HEADER_MAP[key]);
    } else if (SKIP_HEADERS.has(key)) {
      colMap.push(null);
    } else {
      // Unmapped non-skip header → treat as custom field
      colMap.push(null);
      customFieldColMap.push({ colIndex: idx, headerLabel: headerText });
    }
  });

  // Check that we found the enquiryId column
  if (!colMap.includes('enquiryId')) {
    throw new Error('Could not find "Enquiry ID" column in the header row. Make sure you uploaded the correct file.');
  }

  // Parse data rows (index 2+)
  const rows: ParsedEnquiryRow[] = [];

  for (let r = 2; r < raw.length; r++) {
    const rowData = raw[r];
    if (!rowData) continue;

    // Skip empty rows
    const hasAnyData = rowData.some((cell: any) => cell !== null && cell !== undefined && String(cell).trim() !== '');
    if (!hasAnyData) continue;

    const parsed: any = {};
    for (let c = 0; c < colMap.length; c++) {
      const fieldKey = colMap[c];
      if (!fieldKey) continue;

      const cellVal = rowData[c];

      if (fieldKey === 'enquiryId') {
        parsed.enquiryId = typeof cellVal === 'number' ? cellVal : parseInt(String(cellVal), 10) || null;
      } else if (fieldKey === 'dateOfBirth') {
        parsed.dateOfBirth = normalizeDate(cellVal);
      } else {
        parsed[fieldKey] = cellToString(cellVal);
      }
    }

    // Extract custom field values (unmapped headers)
    if (customFieldColMap.length > 0) {
      const customFields: Record<string, any> = {};
      for (const { colIndex, headerLabel } of customFieldColMap) {
        const val = cellToString(rowData[colIndex]);
        if (val !== null) {
          customFields[headerLabel] = val;
        }
      }
      if (Object.keys(customFields).length > 0) {
        parsed.customFields = customFields;
      }
    }

    rows.push(parsed as ParsedEnquiryRow);
  }

  return rows;
}

/**
 * Encode an ArrayBuffer to base64 string for sending to the server.
 */
export function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
}
